import { type ReactElement, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { clearLocalSession, supabase } from './supabaseClient.ts';
import { useTutorSession } from './context/TutorSessionContext.tsx';
import { needsSignupCompletion } from './utils/routeAfterSignIn.ts';

type Gate = 'loading' | 'allow' | 'deny' | 'complete';

export default function RequireStudentOrTutorSession({ children }: { children: ReactElement }) {
  const { isTutorSession } = useTutorSession();
  const location = useLocation();
  const [gate, setGate] = useState<Gate>('loading');

  useEffect(() => {
    if (isTutorSession) {
      setGate('allow');
      return;
    }
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setGate('deny');
        return;
      }
      const { data, error } = await supabase
        .from('students')
        .select('user_id')
        .eq('user_id', user.id)
        .maybeSingle();

      // A failed lookup proves nothing, so bounce but keep the session.
      if (error) {
        setGate('deny');
        return;
      }

      if (!data) {
        // Google (or unfinished email) signup: keep the session and finish
        // year/subjects. Staff/parent leftover sessions still get cleared so
        // they aren't trapped without a sign-out button.
        if (await needsSignupCompletion(user)) {
          setGate('complete');
          return;
        }
        clearLocalSession('/practice');
        return;
      }

      setGate('allow');
    })();
  }, [isTutorSession]);

  if (gate === 'loading') return null;

  if (isTutorSession) {
    if (
      location.pathname.startsWith('/profile') ||
      location.pathname.startsWith('/practice/saved-questions') ||
      location.pathname.startsWith('/practice/mark-disputes')
    ) {
      return <Navigate to="/tests" replace />;
    }
    return children;
  }

  if (gate === 'complete') {
    return <Navigate to="/signup/complete" replace />;
  }

  if (gate !== 'allow') {
    return <Navigate to="/practice" replace />;
  }
  return children;
}
